/**
 * =============================================================================
 * IDS CRM - The "← List" link on a detail page
 * =============================================================================
 *
 * Goes back to the list as it was left: the same sort, filters and page
 * (see utils/listReturn.js). `to` is the page's usual list. `also` names
 * other lists the page is opened from (a job is opened from Scheduling and
 * from Jobs & Tickets): when the page was opened from one of them, that is
 * the list returned to, under its name from `labels`. Opened from anywhere
 * else (Today, a customer, a search), it is the usual list.
 *
 *   <BackToList to="/tickets" className="back-link">← Jobs &amp; Tickets</BackToList>
 *   <BackToList to="/tasks" also={['/tickets']}
 *     labels={{ '/tasks': 'Scheduling', '/tickets': 'Jobs & Tickets' }} />
 *
 * The link carries data-back-to-list so hooks/useUnsavedChanges, which steps
 * in on a part-filled form, still returns to the list as it was left.
 * =============================================================================
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { listReturn, RESTORE_LIST_STATE } from '../utils/listReturn';

function BackToList({ to, also = [], labels = null, className, children }) {
  const location = useLocation();
  const target = listReturn([to, ...also], location.pathname);
  const label = labels && labels[target.path];
  return (
    <Link to={target.to} state={RESTORE_LIST_STATE} className={className} data-back-to-list="">
      {label ? `← ${label}` : children}
    </Link>
  );
}

/**
 * The same return for code that leaves a page itself (after a delete, or a
 * Cancel button): `const backToList = useBackToList('/assets'); backToList();`
 */
export function useBackToList(to, also = []) {
  const navigate = useNavigate();
  const location = useLocation();
  return () => navigate(listReturn([to, ...also], location.pathname).to, { state: RESTORE_LIST_STATE });
}

export default BackToList;
